/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'
import { Box, Divider } from '@mui/material'
import { chunk, compact, flatten, isEmpty, map } from 'lodash'

import axios from '../api/axios'
import { DigitalCertificate } from '../api/index'
import { REACT_APP_ENV } from '../common/constants'
import Legend from '../components/Legend'
import BodyDamages from '../components/pdf/BodyDamages'
import BodyPaintwork from '../components/pdf/BodyPaintwork'
import CarControlDescription from '../components/pdf/CarControlDescription'
import CarData from '../components/pdf/CarData'
import CarRating from '../components/pdf/CarRating'
import CarWorkTodo from '../components/pdf/CarWorkTodo'
import ImageGallery from '../components/pdf/common/ImageGallery'
import EquipmentList from '../components/pdf/EquipmentList'
import InteriorDamages from '../components/pdf/InteriorDamages'
import Page from '../components/pdf/Page'
import TechnicalConditionList from '../components/pdf/TechnicalConditionList'
import TyresCondition from '../components/pdf/TyresCondition'
import Ratings from '../components/Ratings'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import { PointChecked, PointUnchecked, WarnIcon } from '../icons'
import { checkEnvironment } from '../utils/helpers'

const getDamageImages = (conditions, prefix) =>
  chunk(
    compact(
      flatten(
        map(conditions, (element) =>
          map(element[`${prefix}Damages`], (damage) => {
            if (damage[`${prefix}DamageId`] !== 1 && damage.image !== null) {
              return {
                imageUrl: damage.image.imageUrl,
                name: `${element[`${prefix}ElementName`]} - ${
                  damage[`${prefix}Damage`]
                }`,
              }
            }
            return null
          }),
        ),
      ),
    ),
    6,
  )

function ControlsPDF() {
  const { id: digitalCertificateId } = useParams()
  const axiosPrivate = useAxiosPrivate()
  const [digitalCertificate, setDigitalCertificate] = useState({})
  const { t } = useTranslation()

  const legendItems = [
    {
      icon: <PointChecked />,
      label: t('carControl.status.ok'),
      iconColor: 'success',
    },
    {
      icon: <WarnIcon />,
      label: t('carControl.status.notOk'),
      iconColor: 'warning',
    },
    {
      icon: <PointUnchecked />,
      label: t('carControl.status.notApplicable'),
    },
  ]

  useEffect(() => {
    const provider = checkEnvironment(REACT_APP_ENV, 'usedcars')
      ? axios
      : axiosPrivate
    DigitalCertificate.getCertificateData(provider, digitalCertificateId)
      .then((res) => {
        setDigitalCertificate(res.data)
      })
      // eslint-disable-next-line no-console
      .catch((e) => console.error(e))
  }, [axiosPrivate, digitalCertificateId])

  const bodyImages = getDamageImages(digitalCertificate.bodyConditions, 'body')

  const interiorImages = getDamageImages(
    digitalCertificate.interiorConditions,
    'interior',
  )

  const technicalImages = chunk(
    compact(
      flatten(
        map(
          digitalCertificate.technicalConditions
            ?.getTechnicalConditionGroupModel,
          (group) => {
            if (isEmpty(group.technicalImages)) {
              return null
            }

            return map(group.technicalImages, (image) => ({
              imageUrl: image.imageUrl,
              name: group.technicalChecklistGroupName,
            }))
          },
        ),
      ),
    ),
    6,
  )

  return isEmpty(digitalCertificate) ? null : (
    <>
      {/* PAGE */}
      <Page digitalCertificate={digitalCertificate}>
        <CarRating digitalCertificate={digitalCertificate} />
        <CarData digitalCertificate={digitalCertificate} />
        <CarWorkTodo digitalCertificate={digitalCertificate} />
      </Page>
      {/* PAGE */}
      <Page digitalCertificate={digitalCertificate}>
        <Box sx={{ mb: 2 }}>
          <Ratings
            align="center"
            big
            label={t('bodyCondition.title')}
            orientation="vertical"
            score={digitalCertificate.rating?.body || 0}
          />
        </Box>
        <BodyPaintwork digitalCertificate={digitalCertificate} />
        <Box sx={{ mt: 5 }}>
          <BodyDamages digitalCertificate={digitalCertificate} />
        </Box>
      </Page>
      {/* PAGE */}
      {!isEmpty(bodyImages) &&
        map(bodyImages, (images, bk) => (
          <Page key={bk}>
            <ImageGallery title={t('bodyCondition.gallery')} images={images} />
          </Page>
        ))}
      {/* PAGE */}
      <Page digitalCertificate={digitalCertificate}>
        <Box sx={{ mt: 3, mb: 2 }}>
          <Ratings
            align="center"
            big
            label={t('interiorCondition.title')}
            orientation="vertical"
            score={digitalCertificate.rating?.interior || 0}
          />
        </Box>
        <InteriorDamages digitalCertificate={digitalCertificate} />
      </Page>
      {/* PAGE */}
      {!isEmpty(interiorImages) &&
        map(interiorImages, (images, ik) => (
          <Page key={ik}>
            <ImageGallery
              title={t('interiorCondition.gallery')}
              images={images}
            />
          </Page>
        ))}

      {/* PAGE */}
      <Page digitalCertificate={digitalCertificate}>
        <Box sx={{ mb: 2 }}>
          <Ratings
            align="center"
            big
            label={t('equipmentCondition.title')}
            orientation="vertical"
            score={digitalCertificate.rating?.equipment || 0}
          />
        </Box>
        <EquipmentList digitalCertificate={digitalCertificate} />
      </Page>
      {/* PAGE */}
      <Page digitalCertificate={digitalCertificate}>
        <Box sx={{ mb: 2 }}>
          <Ratings
            align="center"
            big
            label={t('tyresCondition.title')}
            orientation="vertical"
            score={digitalCertificate.rating?.tyres || 0}
          />
        </Box>
        <TyresCondition digitalCertificate={digitalCertificate} />
      </Page>
      {/* PAGE */}
      <Page digitalCertificate={digitalCertificate}>
        <Box sx={{ mb: 2 }}>
          <Ratings
            align="center"
            big
            label={t('technicalCondition.title')}
            orientation="vertical"
            score={digitalCertificate.rating?.technical || 0}
          />
        </Box>
        <TechnicalConditionList
          digitalCertificate={digitalCertificate}
          groupIds={[1, 2, 3]}
        />
        <Box mt={1}>
          <Legend items={legendItems} orientation="vertical" align="center" />
        </Box>
      </Page>
      {/* PAGE */}
      <Page digitalCertificate={digitalCertificate}>
        <Box sx={{ mb: 2 }}>
          <Ratings
            align="center"
            big
            label={t('technicalCondition.title')}
            orientation="vertical"
            score={digitalCertificate.rating?.technical || 0}
          />
        </Box>
        <TechnicalConditionList
          digitalCertificate={digitalCertificate}
          groupIds={[4, 5]}
        />
        <Box mt={1}>
          <Legend items={legendItems} orientation="vertical" align="center" />
        </Box>
      </Page>
      {/* PAGE */}
      <Page digitalCertificate={digitalCertificate}>
        <Box sx={{ mb: 2 }}>
          <Ratings
            align="center"
            big
            label={t('technicalCondition.title')}
            orientation="vertical"
            score={digitalCertificate.rating?.technical || 0}
          />
        </Box>
        <TechnicalConditionList
          digitalCertificate={digitalCertificate}
          groupIds={[6, 7]}
        />
        <Box mt={1}>
          <Legend items={legendItems} orientation="vertical" align="center" />
        </Box>
      </Page>
      {/* PAGE */}
      <Page digitalCertificate={digitalCertificate}>
        <Box sx={{ mb: 2 }}>
          <Ratings
            align="center"
            big
            label={t('technicalCondition.title')}
            orientation="vertical"
            score={digitalCertificate.rating?.technical || 0}
          />
        </Box>
        <TechnicalConditionList
          digitalCertificate={digitalCertificate}
          groupIds={[8, 9]}
        />
        <Box mt={1}>
          <Legend items={legendItems} orientation="vertical" align="center" />
        </Box>
      </Page>
      {/* PAGE */}
      {!isEmpty(technicalImages) &&
        map(technicalImages, (images, bk) => (
          <Page key={bk} digitalCertificate={digitalCertificate}>
            <ImageGallery images={images} />
          </Page>
        ))}
      {/* PAGE */}
      {!isEmpty(digitalCertificate.technicalConditions.description) && (
        <Page digitalCertificate={digitalCertificate}>
          <CarControlDescription digitalCertificate={digitalCertificate} />
        </Page>
      )}
    </>
  )
}

export default ControlsPDF
